import { useEffect, useRef, useState } from 'react';

import {
  type AssetDelivery,
  type AssetKind,
  chooseClip,
  fetchVideoModels,
  type Gallery,
  type GalleryAnimation,
  type GalleryAnimationVerdict,
  type GalleryAsset,
  GALLERY_NONE,
  fetchGallery,
  regenerateClip,
  type VideoCatalogueView,
} from './api.ts';
import { useAssetUrls } from './assets.ts';
import { DASHBOARD_POLL_MS, describeError, redirectIfUnauthorised } from './data.tsx';
import { Icon, type IconName } from './Icon.tsx';
import { replaceSearch, useLocation } from './router.tsx';
import { useToast } from './toast.tsx';
import {
  Badge,
  EmptyState,
  Loader,
  Panel,
  Segmented,
  Skeleton,
  type Tone,
  usePanelOpen,
} from './ui.tsx';

/**
 * Every file a production made, not only the one it delivered (US-056).
 *
 * The player answers "what did this become"; this answers "what is it made
 * of" — the voice lines, the stills, the screen captures, the composition
 * source, the plan itself. It is the only place an operator can hear a take
 * that was cut, or see which provider made an image and under what licence,
 * without opening a terminal into the container.
 *
 * Three things it does not do, on purpose:
 *
 * - **It does not filter in the browser.** The step and version controls are
 *   query parameters on `GET /api/contents/:id/assets`, so the list is always a
 *   whole answer and the counts beside each option are of the whole content.
 *   They live in the page's URL, so a filtered gallery is a link.
 * - **It does not hold URLs.** A tile carries an asset id; {@link useAssetUrls}
 *   resolves a fifteen-minute presigned URL per id and renews it — the same
 *   hook, and the same renewal, as the player.
 * - **It does not pretend to have a waveform.** An audio tile draws a
 *   deterministic bar pattern behind its duration and loudness, and says so;
 *   the real thing needs the samples, which are in the bucket and not here.
 */

/** How often the listing is re-read; slower than the page's own three seconds. */
const GALLERY_POLL_MS = DASHBOARD_POLL_MS * 5;

/** How much of a text file the preview reads before it stops. */
const PREVIEW_TEXT_LIMIT = 64 * 1024;

/** The icon each kind of file is drawn with when it has no picture of its own. */
const KIND_ICON: Record<AssetKind, IconName> = {
  image: 'image',
  video: 'device-camera-video',
  audio: 'unmute',
  captions: 'quote',
  document: 'file',
  source: 'terminal',
  render: 'device-camera-video',
  poster: 'image',
};

/** What each kind is called in a badge. */
const KIND_LABEL: Record<AssetKind, string> = {
  image: 'image',
  video: 'video',
  audio: 'audio',
  captions: 'captions',
  document: 'document',
  source: 'source',
  render: 'render',
  poster: 'poster',
};

export function AssetGallery({ contentId }: { readonly contentId: string }) {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const step = params.get('step') ?? 'all';
  const version = params.get('version') ?? 'all';

  const [gallery, setGallery] = useState<Gallery | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<GalleryAsset | null>(null);
  /** The clip the **Re-generate with…** dialog is open on, or `null`. */
  const [reroll, setReroll] = useState<GalleryAsset | null>(null);
  /** The asset id a re-roll or a choice is running for; one at a time. */
  const [busy, setBusy] = useState<string | null>(null);
  const toast = useToast();
  // Folded by default; the listing is read, and re-read, only while unfolded.
  const [open, setOpen] = usePanelOpen('assets');

  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    let cancelled = false;
    const read = (): void => {
      fetchGallery(contentId, { step: asFilter(step), version: asFilter(version) }, controller.signal)
        .then((next) => {
          if (cancelled) return;
          setGallery(next);
          setError(null);
        })
        .catch((cause: unknown) => {
          if (cancelled || controller.signal.aborted || redirectIfUnauthorised(cause)) return;
          setError(describeError(cause));
        });
    };
    read();
    // The gallery grows as steps end, so it is re-read while the tab is
    // watched — on its own slower beat, because it is a bigger answer than the
    // content row the page polls.
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') read();
    }, GALLERY_POLL_MS);
    return () => {
      cancelled = true;
      controller.abort();
      window.clearInterval(timer);
    };
  }, [contentId, step, version, open]);

  // One presigned URL per tile on screen, renewed before it expires. The filter
  // is what bounds this: a narrowed gallery resolves what it shows.
  // An animation also draws its source and links its contact sheet (US-011),
  // which a filtered gallery may not be showing as tiles of their own.
  const shownAssets = open ? (gallery?.assets ?? []) : [];
  const deliveries = useAssetUrls(contentId, [
    ...new Set(
      shownAssets.flatMap((asset) => [
        asset.id,
        ...(asset.animation === null
          ? []
          : [asset.animation.sourceAssetId, asset.animation.sheetAssetId].filter(
              (id): id is string => id !== null,
            )),
      ]),
    ),
  ]);

  const setParam = (key: string, value: string): void => {
    const next = new URLSearchParams(window.location.search);
    // 'all' is the default of both controls and does not belong in the URL, so
    // an unfiltered gallery has a clean address.
    if (value === 'all') next.delete(key);
    else next.set(key, value);
    replaceSearch(next);
  };

  const copyLink = (asset: GalleryAsset, delivery: AssetDelivery | undefined): void => {
    if (delivery === undefined) return;
    const absolute = new URL(delivery.url, window.location.origin).href;
    navigator.clipboard
      .writeText(absolute)
      .then(() => {
        toast.ok(
          delivery.expiresInSeconds === null
            ? `Link to ${asset.name} copied.`
            : `Link to ${asset.name} copied. It stops working in ${String(
                Math.round(delivery.expiresInSeconds / 60),
              )} minutes.`,
        );
      })
      .catch((cause: unknown) => {
        toast.error(describeError(cause));
      });
  };

  /**
   * **Use this clip**: the compose and render steps that put it in the next
   * version. The gallery is re-read afterwards rather than patched — the
   * server has just changed the plan, and what the steps filter offers changes
   * with it.
   */
  const choose = (asset: GalleryAsset): Promise<void> => {
    setBusy(asset.id);
    return chooseClip(contentId, asset.id)
      .then((result) => {
        toast.ok(result.message);
      })
      .catch((cause: unknown) => {
        if (!redirectIfUnauthorised(cause)) toast.error(describeError(cause));
      })
      .finally(() => {
        setBusy(null);
      });
  };

  const regenerate = (asset: GalleryAsset, model: string): Promise<void> => {
    setBusy(asset.id);
    setReroll(null);
    return regenerateClip(contentId, asset.id, model)
      .then((result) => {
        toast.ok(result.message);
        return fetchGallery(contentId, { step: asFilter(step), version: asFilter(version) }).then(
          (next) => {
            setGallery(next);
          },
        );
      })
      .catch((cause: unknown) => {
        if (!redirectIfUnauthorised(cause)) toast.error(describeError(cause));
      })
      .finally(() => {
        setBusy(null);
      });
  };

  const filtered = step !== 'all' || version !== 'all';

  return (
    <Panel
      title="Assets"
      icon="stack"
      id="assets"
      collapsible
      defaultOpen={false}
      open={open}
      onOpenChange={setOpen}
      summary="every file the production wrote"
      meta={
        gallery === null || !open ? null : (
          <span className="panel__meta">
            <span>
              {filtered
                ? `${String(gallery.assets.length)} of ${String(gallery.total)} files`
                : `${String(gallery.total)} ${gallery.total === 1 ? 'file' : 'files'}`}
            </span>
          </span>
        )
      }
      actions={
        gallery !== null &&
        (gallery.steps.length > 1 || gallery.versions.length > 1) && (
          <div className="gallery__filters">
            {gallery.versions.length > 1 && (
              <Segmented
                ariaLabel="Version"
                value={version}
                options={[
                  { value: 'all', label: 'All' },
                  ...gallery.versions.map((option) => ({
                    value: option.version === null ? GALLERY_NONE : String(option.version),
                    label: option.version === null ? 'Shared' : `v${String(option.version)}`,
                    count: option.count,
                  })),
                ]}
                onChange={(value) => {
                  setParam('version', value);
                }}
              />
            )}
            {gallery.steps.length > 1 && (
              <select
                className="field__input field__input--inline"
                value={step}
                aria-label="Step"
                onChange={(event) => {
                  setParam('step', event.target.value);
                }}
              >
                <option value="all">Every step</option>
                {gallery.steps.map((option) => (
                  <option key={option.stepId} value={option.stepId}>
                    {option.stepId === GALLERY_NONE
                      ? `No step (${String(option.count)})`
                      : `${option.stepId} · ${option.title} (${String(option.count)})`}
                  </option>
                ))}
              </select>
            )}
          </div>
        )
      }
    >
      {error !== null ? (
        <EmptyState icon="alert" title="The assets could not be read">
          <p>{error}</p>
        </EmptyState>
      ) : gallery === null ? (
        <Skeleton lines={4} />
      ) : gallery.assets.length === 0 ? (
        <EmptyState
          icon="stack"
          title={filtered ? 'Nothing matches this filter' : 'No files yet'}
        >
          <p>
            {filtered
              ? 'Every file this content has is one choice away — set the filters back to all.'
              : 'Files appear here as steps end: each one is mirrored to object storage right after the step that wrote it.'}
          </p>
        </EmptyState>
      ) : (
        <ul className="gallery">
          {gallery.assets.map((asset) => (
            <AssetTile
              key={asset.id}
              asset={asset}
              delivery={deliveries.get(asset.id)}
              onReroll={() => {
                setReroll(asset);
              }}
              onChoose={() => {
                void choose(asset);
              }}
              busy={busy === asset.id}
              onPreview={() => {
                setPreview(asset);
              }}
              onCopy={() => {
                copyLink(asset, deliveries.get(asset.id));
              }}
            />
          ))}
        </ul>
      )}

      <RerollDialog
        asset={reroll}
        onClose={() => {
          setReroll(null);
        }}
        onGenerate={(model) => {
          void regenerate(reroll as GalleryAsset, model);
        }}
      />

      <PreviewDialog
        asset={preview}
        delivery={preview === null ? undefined : deliveries.get(preview.id)}
        deliveries={deliveries}
        onClose={() => {
          setPreview(null);
        }}
      />
    </Panel>
  );
}

/** `'all'` is this view's word for "no filter"; the server's is no parameter. */
function asFilter(value: string): string | null {
  return value === 'all' ? null : value;
}

/**
 * One file: a thumbnail that opens it, what is known about it, and the two
 * ways to take it away.
 *
 * The thumbnail is a `<button>` rather than a decorated `<div>`: click-to-
 * preview has to be reachable from the keyboard, and a picture that does
 * something is a control.
 */
function AssetTile({
  asset,
  delivery,
  onPreview,
  onCopy,
  onReroll,
  onChoose,
  busy,
}: {
  readonly asset: GalleryAsset;
  readonly delivery: AssetDelivery | undefined;
  readonly onPreview: () => void;
  readonly onCopy: () => void;
  /** Opens the model picker; only a generated clip has one. */
  readonly onReroll: () => void;
  readonly onChoose: () => void;
  readonly busy: boolean;
}) {
  return (
    <li className="tile">
      <button
        type="button"
        className="tile__thumb"
        onClick={onPreview}
        // An animation opens without a URL too: what it was made from is the
        // point, and a fallback has no clip to fetch at all (US-011).
        disabled={delivery === undefined && asset.animation === null}
        title={
          delivery === undefined
            ? `${asset.path} · ${asset.uploaded ? 'fetching…' : 'not in object storage yet'}`
            : `Preview ${asset.path}`
        }
      >
        <Thumbnail asset={asset} delivery={delivery} />
      </button>

      <div className="tile__body">
        <p className="tile__name mono" title={asset.path}>
          {asset.name}
        </p>
        <p className="tile__facts">{facts(asset).join(' · ')}</p>

        <p className="tile__badges">
          <Badge tone="neutral">{KIND_LABEL[asset.kind]}</Badge>
          {asset.version !== null && <Badge tone="final">v{asset.version}</Badge>}
          {asset.step !== null && (
            <Badge tone="ready" title={asset.step.title}>
              {asset.step.stepId}
            </Badge>
          )}
          {/* The provenance of generated material, when a provider call
              recorded it. Nothing is drawn for a file nobody was billed for. */}
          {asset.provider !== null && (
            <Badge
              tone="active"
              title={`${asset.provider.operation}${
                asset.provider.model === null ? '' : ` · ${asset.provider.model}`
              } · $${asset.provider.costUsd.toFixed(2)}`}
            >
              {asset.provider.name}
            </Badge>
          )}
          {asset.animation !== null && (
            <Badge
              tone="review"
              title={`Animated from ${asset.animation.source} · ${VERDICT_LABEL[asset.animation.verdict]}`}
            >
              AI-animated
            </Badge>
          )}
          {asset.licence !== null && <Badge tone="done">{asset.licence}</Badge>}
          {asset.revisions > 1 && (
            <Badge tone="neutral" title="This path has been written more than once">
              {asset.revisions} revisions
            </Badge>
          )}
          {!asset.uploaded && (
            <Badge tone="wait" title="It is in the workspace, but not yet in the bucket">
              not uploaded
            </Badge>
          )}
        </p>

        {asset.attribution !== null && (
          <p className="tile__attribution" title="Attribution recorded for this file">
            {asset.attribution}
          </p>
        )}

        <p className="tile__actions">
          {delivery === undefined ? (
            <span className="button button--quiet" aria-disabled="true">
              <Icon name="file" />
              Download
            </span>
          ) : (
            <a
              className="button button--quiet"
              href={delivery.url}
              download={asset.name}
              title={asset.path}
            >
              <Icon name="file" />
              Download
            </a>
          )}
          <button
            type="button"
            className="button button--quiet"
            onClick={onCopy}
            disabled={delivery === undefined}
            title={
              delivery?.expiresInSeconds === null
                ? 'Copy a link to this file'
                : 'Copy a link to this file; it stops working in fifteen minutes'
            }
          >
            <Icon name="copy" />
            Copy link
          </button>

          {/* Only a clip this content generated: the prompt on its row is what
              a second call sends, and nothing else here has one (US-086). */}
          {asset.clip !== null && (
            <>
              <button
                type="button"
                className="button button--quiet"
                onClick={onReroll}
                disabled={busy}
                title={`Generate this prompt again on another model. It spends: the clip-length and clips-per-video caps and both spend caps apply exactly as they do inside a step.`}
              >
                <Icon name="sync" />
                Re-generate with…
              </button>
              <button
                type="button"
                className="button button--quiet"
                onClick={onChoose}
                disabled={busy}
                title="Compose the next version with this clip and render it"
              >
                <Icon name="check" />
                {busy ? 'Working…' : 'Use this clip'}
              </button>
            </>
          )}
        </p>

        {busy && <Loader label="Asking the server for this clip…" size="sm" />}

        {asset.clip !== null && (
          <p className="tile__attribution" title="What this clip was generated from">
            {asset.clip.model} · “{asset.clip.prompt}”
            {asset.clip.rerollOf === null ? '' : ` · a re-roll of ${asset.clip.rerollOf}`}
          </p>
        )}
      </div>
    </li>
  );
}

/**
 * The picture on a tile.
 *
 * A still is itself, loaded lazily straight from the bucket. Everything else is
 * its kind's icon on the sunken stage — with, for audio, a bar pattern that is
 * a *placeholder* and not a waveform: it is derived from the asset id so it
 * does not dance between polls, and the numbers beside it are what the probe
 * actually measured.
 */
function Thumbnail({
  asset,
  delivery,
}: {
  readonly asset: GalleryAsset;
  readonly delivery: AssetDelivery | undefined;
}) {
  if (isPicture(asset) && delivery !== undefined) {
    return (
      <img
        className="tile__image"
        src={delivery.url}
        alt={asset.path}
        loading="lazy"
        decoding="async"
      />
    );
  }
  if (asset.kind === 'audio') {
    return (
      <span className="tile__waveform" aria-hidden="true">
        {bars(asset.id).map((height, index) => (
          <span
            // The bars have no identity of their own; the index is the only key.
            key={index}
            className="tile__bar"
            style={{ height: `${String(height)}%` }}
          />
        ))}
      </span>
    );
  }
  return <Icon name={KIND_ICON[asset.kind]} className="tile__icon" />;
}

/** Whether the file is something an `<img>` can draw. */
function isPicture(asset: GalleryAsset): boolean {
  return asset.mime.startsWith('image/') && asset.mime !== 'image/svg+xml';
}

/**
 * Twenty-four bar heights from the asset id.
 *
 * Deterministic on purpose: a random pattern would redraw on every poll, and a
 * pattern that moves suggests it is measuring something. It is not — it is the
 * placeholder the story asked for, and the duration and loudness beside it are
 * the real measurements.
 */
function bars(id: string): number[] {
  const heights: number[] = [];
  for (let index = 0; index < 24; index += 1) {
    const code = id.charCodeAt(index % id.length) + index * 7;
    heights.push(20 + ((code * 31) % 80));
  }
  return heights;
}

/** The one line under the name: what the probe measured about this file. */
function facts(asset: GalleryAsset): string[] {
  const out: string[] = [];
  if (asset.width !== null && asset.height !== null) {
    out.push(`${String(asset.width)}×${String(asset.height)}`);
  }
  if (asset.durationMs !== null) out.push(`${(asset.durationMs / 1000).toFixed(1)} s`);
  if (asset.loudnessLufs !== null) out.push(`${asset.loudnessLufs.toFixed(1)} LUFS`);
  if (asset.bytes !== null) out.push(size(asset.bytes));
  if (asset.directory !== '') out.push(asset.directory);
  return out;
}

function size(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${String(Math.max(1, Math.round(bytes / 1024)))} kB`;
}

/**
 * One file, full size, on the native `<dialog>`.
 *
 * A picture and a video play from the bucket; a text file is read through the
 * same-origin proxy, because a presigned URL is a third-party origin and
 * `fetch` would need a CORS rule this app does not require anyone to write.
 * Anything else offers the two things that always work — open it, or download
 * it.
 */
function PreviewDialog({
  asset,
  delivery,
  deliveries,
  onClose,
}: {
  readonly asset: GalleryAsset | null;
  readonly delivery: AssetDelivery | undefined;
  /** Every URL resolved so far, which an animation's source and sheet come from. */
  readonly deliveries: ReadonlyMap<string, AssetDelivery>;
  readonly onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [text, setText] = useState<string | null>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (dialog === null) return;
    // `showModal()` throws if it is already open, which StrictMode's double
    // invocation would otherwise trigger.
    if (asset !== null && !dialog.open) dialog.showModal();
    if (asset === null && dialog.open) dialog.close();
  }, [asset]);

  const rawUrl = asset?.rawUrl ?? null;
  const readable = asset !== null && isText(asset);

  useEffect(() => {
    setText(null);
    if (rawUrl === null || !readable) return;
    const controller = new AbortController();
    fetch(rawUrl, { signal: controller.signal })
      .then(async (response) => (response.ok ? response.text() : Promise.reject(new Error())))
      .then((body) => {
        setText(body.slice(0, PREVIEW_TEXT_LIMIT));
      })
      .catch(() => {
        setText(null);
      });
    return () => {
      controller.abort();
    };
  }, [rawUrl, readable]);

  return (
    <dialog
      className="lightbox"
      ref={ref}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
    >
      {asset !== null && (
        <>
          <div className="lightbox__head">
            <p className="lightbox__title mono" title={asset.path}>
              {asset.path}
            </p>
            <button type="button" className="button button--quiet" onClick={onClose}>
              <Icon name="x" />
              Close
            </button>
          </div>

          <div className="lightbox__stage">
            {delivery === undefined && asset.animation?.verdict === 'fallback' ? (
              <p className="lightbox__note">
                This animation was given up on, and the step is served by its still
                {asset.animation.fallbackStill === null ? '' : ` ${asset.animation.fallbackStill}`}
                . There is no clip at this path to play.
              </p>
            ) : delivery === undefined ? (
              <p className="lightbox__note">
                This file has no URL yet. It is mirrored to object storage after the step that
                wrote it; until then there is nothing to fetch.
              </p>
            ) : isPicture(asset) ? (
              <img className="lightbox__image" src={delivery.url} alt={asset.path} />
            ) : asset.mime.startsWith('video/') ? (
              <video className="lightbox__video" controls playsInline preload="metadata">
                <source src={delivery.url} type={asset.mime} />
              </video>
            ) : asset.mime.startsWith('audio/') ? (
              <audio className="lightbox__audio" controls preload="metadata" src={delivery.url} />
            ) : readable ? (
              <pre className="lightbox__text">{text ?? 'Reading…'}</pre>
            ) : (
              <p className="lightbox__note">
                Nothing in the browser renders {asset.mime}. Open it in a new tab, or download it.
              </p>
            )}
          </div>

          {asset.animation !== null && (
            <AnimationDetails animation={asset.animation} deliveries={deliveries} />
          )}

          <div className="lightbox__bar">
            <span className="lightbox__facts">{facts(asset).join(' · ')}</span>
            {delivery !== undefined && (
              <span className="lightbox__actions">
                <a
                  className="button button--quiet"
                  href={delivery.url}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Icon name="link-external" />
                  Open
                </a>
                <a
                  className="button button--quiet"
                  href={delivery.url}
                  download={asset.name}
                >
                  <Icon name="file" />
                  Download
                </a>
              </span>
            )}
          </div>
        </>
      )}
    </dialog>
  );
}

/** What each verdict reads as, beside the reason it was given. */
const VERDICT_LABEL: Record<GalleryAnimationVerdict, string> = {
  unchecked: 'not checked yet',
  pass: 'passed its check',
  fail: 'failed its check',
  fallback: 'fell back to the still',
};

const VERDICT_TONE: Record<GalleryAnimationVerdict, Tone> = {
  unchecked: 'wait',
  pass: 'done',
  fail: 'danger',
  fallback: 'wait',
};

/**
 * What an animation was animated from, and how its check went (US-011): the
 * source beside the ask, the spend of every attempt together, and the contact
 * sheet the verdict was given on — what an operator judges it by at approval.
 */
function AnimationDetails({
  animation,
  deliveries,
}: {
  readonly animation: GalleryAnimation;
  readonly deliveries: ReadonlyMap<string, AssetDelivery>;
}) {
  const source =
    animation.sourceAssetId === null ? undefined : deliveries.get(animation.sourceAssetId);
  const sheet =
    animation.sheetAssetId === null ? undefined : deliveries.get(animation.sheetAssetId);
  return (
    <div className="animation">
      <div className="animation__source">
        {source === undefined ? (
          <Icon name="image" className="tile__icon" />
        ) : (
          <img className="animation__thumb" src={source.url} alt={animation.source} />
        )}
      </div>
      <dl className="animation__facts">
        <dt>Animated from</dt>
        <dd className="mono" title={animation.source}>
          {animation.source}
        </dd>
        <dt>Motion</dt>
        <dd>“{animation.motion}”</dd>
        <dt>Model</dt>
        <dd className="mono">{animation.model}</dd>
        <dt>Attempts</dt>
        <dd>{animation.attempts}</dd>
        <dt>Cost</dt>
        <dd>
          ${animation.costUsd.toFixed(2)}
          {animation.attempts > 1 ? ' across every attempt' : ''}
        </dd>
        <dt>Verdict</dt>
        <dd>
          <Badge tone={VERDICT_TONE[animation.verdict]}>{VERDICT_LABEL[animation.verdict]}</Badge>
          {animation.reason === null ? null : <span> {animation.reason}</span>}
        </dd>
        <dt>Check</dt>
        <dd>
          {sheet === undefined ? (
            <span className="mono" title="Drawn by mc animate check; not in object storage yet">
              {animation.sheet}
            </span>
          ) : (
            <a href={sheet.url} target="_blank" rel="noreferrer">
              Contact sheet
            </a>
          )}
        </dd>
      </dl>
    </div>
  );
}

/** Whether the file is something a `<pre>` can show honestly. */
function isText(asset: GalleryAsset): boolean {
  return (
    asset.mime.startsWith('text/') ||
    asset.mime === 'application/json' ||
    asset.mime === 'application/javascript' ||
    asset.mime === 'image/svg+xml'
  );
}

/**
 * **Re-generate with…**: the same prompt, on a model the operator picks.
 *
 * The list is OpenRouter's own catalogue, read through the endpoint Settings
 * reads — so it is the live sheet with live prices, and the model that made
 * the clip is marked rather than hidden: re-rolling on the same model is a
 * second take, which is a legitimate thing to want and a second charge either
 * way. A catalogue that cannot be reached falls back to a text field, because
 * a network this server cannot reach is not a reason an operator may not name
 * a model they know.
 */
function RerollDialog({
  asset,
  onClose,
  onGenerate,
}: {
  readonly asset: GalleryAsset | null;
  readonly onClose: () => void;
  readonly onGenerate: (model: string) => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [catalogue, setCatalogue] = useState<VideoCatalogueView | null>(null);
  const [failed, setFailed] = useState(false);
  const [model, setModel] = useState('');

  useEffect(() => {
    const dialog = ref.current;
    if (dialog === null) return;
    if (asset !== null && !dialog.open) dialog.showModal();
    if (asset === null && dialog.open) dialog.close();
  }, [asset]);

  useEffect(() => {
    if (asset === null) return;
    setModel('');
    const controller = new AbortController();
    fetchVideoModels(controller.signal)
      .then((view) => {
        setCatalogue(view);
        setFailed(false);
      })
      .catch(() => {
        setFailed(true);
      });
    return () => {
      controller.abort();
    };
  }, [asset]);

  const models = catalogue?.models ?? [];
  const listed = models.length > 0 && !failed;

  return (
    <dialog
      className="lightbox"
      ref={ref}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
    >
      {asset !== null && asset.clip !== null && (
        <>
          <div className="lightbox__head">
            <p className="lightbox__title mono">Re-generate {asset.name}</p>
            <button type="button" className="button button--quiet" onClick={onClose}>
              <Icon name="x" />
              Close
            </button>
          </div>

          <div className="lightbox__stage lightbox__stage--form">
            <p className="lightbox__note">
              The same prompt, on another model: “{asset.clip.prompt}” —{' '}
              {String(asset.clip.seconds)} s{' '}
              {asset.clip.resolution === null ? '' : `${asset.clip.resolution} `}
              {asset.clip.reference === null ? '' : `from ${asset.clip.reference} `}— made
              now by {asset.clip.model}. It lands as a new file beside this one and is
              recorded against the same step; nothing that has been rendered changes until
              you choose it. It spends, under the same caps a step spends under.
            </p>

            <label className="field" htmlFor="reroll-model">
              <span className="field__label">Model</span>
              {listed ? (
                <select
                  id="reroll-model"
                  className="field__input"
                  value={model}
                  onChange={(event) => {
                    setModel(event.target.value);
                  }}
                >
                  <option value="">Choose a model…</option>
                  {models.map((option) => (
                    <option key={option.model} value={option.model}>
                      {option.name}
                      {option.usdPerSecond === null
                        ? ''
                        : ` · $${option.usdPerSecond.toFixed(4)}/s`}
                      {option.model === asset.clip?.model ? ' · this clip’s model' : ''}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  id="reroll-model"
                  className="field__input"
                  value={model}
                  placeholder="openai/sora-2"
                  onChange={(event) => {
                    setModel(event.target.value);
                  }}
                />
              )}
              <span className="field__hint">
                {listed
                  ? `${String(models.length)} models, priced from OpenRouter’s own catalogue.`
                  : 'The catalogue could not be read, so name the model yourself — the call is ' +
                    'made against whatever you type.'}
              </span>
            </label>
          </div>

          <div className="lightbox__bar">
            <span className="lightbox__facts">
              {catalogue === null
                ? 'Reading the catalogue…'
                : `Clips are capped at ${String(catalogue.maxClips)} per content.`}
            </span>
            <span className="lightbox__actions">
              <button
                type="button"
                className="button"
                disabled={model.trim() === ''}
                onClick={() => {
                  onGenerate(model.trim());
                }}
              >
                <Icon name="sync" />
                Generate
              </button>
            </span>
          </div>
        </>
      )}
    </dialog>
  );
}
